import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import debounce from 'lodash/debounce';

import type { DocNode } from '@atlaskit/adf-schema';
import { EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { JSONNode } from '@atlaskit/editor-json-transformer';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { FlagsProvider } from '@atlaskit/flag';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import type { PromptContentChangePayload } from '@atlassian/conversation-assistant-chat-prompt-input/prompt-content-change-payload';
import { RovoInlineChatPrompt } from '@atlassian/conversation-assistant-chat-prompt-input/ui/inline-chat-prompt';
import {
	remixEditExistingExperience,
	REMIX_EDIT_EXISTING_MARKS,
} from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { Breakpoint } from '@atlassian/generative-ai-modal/hooks/useElementBreakpoints';
import { ROVO_CHAT_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/rovo-chat-client';
import { ROVO_EXPERIENCE_TRACKING_SENDER_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/rovo-experience-tracking-sender-client';
import { AifcExperienceTrackingCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/aifc-preview-experience-tracking-command';
import { getBrowserBridgeAPI } from '@atlassian/rovo-content-bridge-api/singleton';

import { resetSessionState } from '../../../../pm-plugins/ai-session-context/commands';
import {
	createCommitInlineRovoStreamingCommand,
	createEndInlineRovoStreamingCommand,
} from '../../../../pm-plugins/decoration/actions';
import type { EditorPluginAIProvider } from '../../../../types/types';
import { useContentComponentContext } from '../../../hooks/useContentComponentContext';
import { CONFIG_ITEM_KEYS } from '../../../prebuilt/config-items/config-item-keys';
import type { UserFlowAEP } from '../../../utils/analytics/analyticsFlowTypes';

const providerProductToProuductKey = (product: EditorPluginAIProvider['product']) => {
	switch (product) {
		case 'CONFLUENCE':
			return 'confluence';
		case 'JSW':
		case 'JSM':
		case 'JWM':
		case 'JPD':
			return 'jira';
		case 'ATLAS':
		case 'TOWNSQUARE':
			return 'atlas';
		case 'BITBUCKET':
			return 'bitbucket';
		default:
			return 'unknown';
	}
};

const backupChannelVariables = {
	cloudId: '',
	orgId: undefined,
};

const isMauiAppNode = (node?: JSONNode) =>
	node?.type === 'extension' &&
	node?.attrs != null &&
	'extensionKey' in node.attrs &&
	node.attrs.extensionKey === 'native-embed:maui';

const isMediaNode = (node?: JSONNode) => {
	if (!node) {
		return false;
	}

	if (node.type === 'media') {
		return true;
	}

	if (node.type === 'mediaSingle' && Array.isArray(node.content) && node.content.length > 0) {
		return (node.content[0] as JSONNode | undefined)?.type === 'media';
	}

	return false;
};

const getSelectionLabsLozengeVariant = (
	selectionFragment: JSONNode[] | null,
): 'default' | 'maui' | undefined => {
	if (!Array.isArray(selectionFragment) || selectionFragment.length === 0) {
		return undefined;
	}

	if (
		selectionFragment.some((node) => isMauiAppNode(node)) ||
		selectionFragment.some((node) => isMediaNode(node))
	) {
		return 'maui';
	}

	return undefined;
};

interface RovoPromptScreenWithLogicProps {
	cancel: (options?: { shouldClearSelectionContext?: boolean }) => void;
	/**
	 * When provided, intercepts the prompt submit to open the Rovo chat sidebar
	 * instead of performing an AI inline edit. The callback receives the prompt
	 * DocNode and should return `true` to indicate it handled the submit.
	 */
	openInRovoChatConfig?: {
		onBeforeSubmit: (prompt: DocNode) => boolean;
	};
}

// eslint-disable-next-line jsdoc/require-jsdoc
export function RovoPromptScreenWithLogic({
	cancel,
	openInRovoChatConfig,
}: RovoPromptScreenWithLogicProps): React.JSX.Element | null {
	const {
		setIsRovoLoading,
		api,
		targetId,
		userADFInput,
		editorPluginAIProvider,
		appearance,
		setCancelRovoPrompt,
		popupsMountPoint,
		editorView,
	} = useContentComponentContext();

	const { cloudId, orgId } = useMemo(
		() => editorPluginAIProvider?.getChannelVariables?.('rovo-agent') || backupChannelVariables,
		[editorPluginAIProvider],
	);
	const product = editorPluginAIProvider.product;
	const productKey = providerProductToProuductKey(product);
	const isPageEditor =
		appearance === 'full-page' || appearance === 'full-width' || appearance === 'max';
	const source =
		productKey === 'confluence' && isPageEditor ? 'CONFLUENCE_INLINE_EDITOR' : 'INLINE_EDITOR';

	const { width } = useSharedPluginStateWithSelector(api, ['width'], (states) => {
		return {
			width: states.widthState?.width,
		};
	});

	const nextSessionId = useSharedPluginStateWithSelector(
		api,
		['aiExperience'],
		({ aiExperienceState }) => aiExperienceState?.aiDecorationExperiencePluginState.nextSessionId,
	);

	const existingShowLabsLozenge = useMemo(() => {
		if (productKey === 'confluence') {
			if (!fg('aifc_onboarding_ga_enabled')) {
				return true;
			}

			// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
			if (!FeatureGates.getExperimentValue('cc-aifc-databases-experiment', 'isEnabled', false)) {
				return true;
			}
		}

		return false;
	}, [productKey]);

	const selectionContext = useSharedPluginStateWithSelector(
		api,
		['aiExperience'],
		({ aiExperienceState }) => aiExperienceState?.selectionContext ?? null,
	);

	const selectionLabsLozengeVariant = useMemo(
		() => getSelectionLabsLozengeVariant(selectionContext?.selectionFragment ?? null),
		[selectionContext],
	);

	const isMauiOrMediaSelection =
		expValEquals('cc-maui-experiment', 'isEnabled', true) &&
		selectionLabsLozengeVariant !== undefined;

	const showLabsLozenge =
		(existingShowLabsLozenge && selectionLabsLozengeVariant === undefined) ||
		isMauiOrMediaSelection;

	useEffect(() => {
		setCancelRovoPrompt?.(undefined);
		return () => {
			setIsRovoLoading(false);
		};
	}, [setCancelRovoPrompt, setIsRovoLoading]);

	const handleAbort = useCallback(
		({ streamEnded }: { streamEnded: boolean }) => {
			cancel({ shouldClearSelectionContext: streamEnded });
			setIsRovoLoading(false);
			if (isMauiOrMediaSelection) {
				remixEditExistingExperience.abort({
					metadata: { reason: 'user_cancel' },
				});
				void getBrowserBridgeAPI()?.send(AifcExperienceTrackingCommand, {
					status: 'abort',
					reason: 'user_cancel',
					experienceName: 'aiMateRemixRegenerate',
					senderId: ROVO_EXPERIENCE_TRACKING_SENDER_CLIENT_ID,
					receiverId: ROVO_CHAT_CLIENT_ID,
				});
			}
			if (
				// eslint-disable-next-line @atlaskit/platform/no-preconditioning -- gate + experiment layering for MAUI rollout (cc-maui-phase-2 + cc-maui-experiment); will simplify post-rollout
				editorView &&
				fg('cc-maui-phase-2') &&
				expValEqualsNoExposure('cc-maui-experiment', 'isEnabled', true)
			) {
				createEndInlineRovoStreamingCommand()(editorView.state, editorView.dispatch);
			}
			api?.core?.actions?.execute(resetSessionState());
		},
		[cancel, setIsRovoLoading, api?.core?.actions, editorView, isMauiOrMediaSelection],
	);

	const onSubmit = useCallback(
		(cancel: () => void) => {
			// Promote the staged target id (set by the toolbar handler) — this is
			// what triggers the consumer-visible loading overlay. No-op when
			// nothing is staged (e.g. modal opened via a non-toolbar path).
			// `expValEqualsNoExposure` because the toolbar click is the designated
			// `cc-maui-experiment` exposure point.
			if (
				// eslint-disable-next-line @atlaskit/platform/no-preconditioning -- gate + experiment layering for MAUI rollout (cc-maui-phase-2 + cc-maui-experiment); will simplify post-rollout
				editorView &&
				fg('cc-maui-phase-2') &&
				expValEqualsNoExposure('cc-maui-experiment', 'isEnabled', true)
			) {
				createCommitInlineRovoStreamingCommand()(editorView.state, editorView.dispatch);
			}

			// Start remixRegenerate when editing an embedded MAUI/media node
			if (isMauiOrMediaSelection) {
				remixEditExistingExperience.start();
				remixEditExistingExperience.mark(REMIX_EDIT_EXISTING_MARKS.SUBMIT);
				// remixType is 'infographic' for media nodes; for MAUI apps it's
				// only known from the backend response (added via updateAttributes
				// in useEditorRovoBridgeCommandListener when the stream settles).
				const isMediaSelection = selectionContext?.selectionFragment?.some(
					(node: { type?: string }) => node.type === 'mediaSingle' || node.type === 'media',
				);
				void getBrowserBridgeAPI()?.send(AifcExperienceTrackingCommand, {
					status: 'start',
					experienceName: 'aiMateRemixRegenerate',
					actionId: selectionContext?.localIds?.[0],
					...(isMediaSelection && { remixType: 'infographic' }),
					senderId: ROVO_EXPERIENCE_TRACKING_SENDER_CLIENT_ID,
					receiverId: ROVO_CHAT_CLIENT_ID,
				});
			}

			editorPluginAIProvider.onSubmit?.();
			setIsRovoLoading(true);
			// Fire prompt event for AIFC. We don't have access to the request id as it's not generated
			// at this point of time and nothing else is available at this stage.
			// For now this will just give us an indication of prompt volume.
			api?.analytics?.actions.fireAnalyticsEvent<UserFlowAEP, 'customEventType'>({
				action: 'prompt',
				actionSubject: 'editorPluginAI',
				actionSubjectId: 'userFlow',
				attributes: {
					aiGroupId: nextSessionId,
					experienceName: CONFIG_ITEM_KEYS.FREE_GENERATE_AIFC,
					actionTaken: '',
					editCount: 0,
					aiSessionId: '',
					inlineStreamingAvailable: true,
				},
				eventType: EVENT_TYPE.UI,
			});

			const cancelRovoPrompt = () => {
				cancel();
			};
			// give DOM time to update the screen
			setTimeout(() => {
				setCancelRovoPrompt?.(() => cancelRovoPrompt);
			}, 100);
		},
		[
			api,
			nextSessionId,
			editorPluginAIProvider,
			setCancelRovoPrompt,
			setIsRovoLoading,
			editorView,
			isMauiOrMediaSelection,
			selectionContext?.localIds,
			selectionContext?.selectionFragment,
		],
	);

	const firePromptInputEngaged = useRef(
		debounce((payload: PromptContentChangePayload, fireEvent: typeof api) => {
			fireEvent?.analytics?.actions.fireAnalyticsEvent<UserFlowAEP, 'customEventType'>({
				action: 'promptInputUpdated',
				actionSubject: 'editorPluginAI',
				actionSubjectId: 'userFlow',
				attributes: {
					aiGroupId: nextSessionId,
					experienceName: CONFIG_ITEM_KEYS.FREE_GENERATE_AIFC,
					actionTaken: '',
					editCount: 0,
					aiSessionId: '',
					inlineStreamingAvailable: true,
					characterCount: payload.textLength,
					hasMentionOrInlineCard: payload.hasMentionOrInlineCard,
				},
				eventType: EVENT_TYPE.UI,
			});
		}, 500),
	).current;

	useEffect(() => {
		return () => {
			firePromptInputEngaged.cancel();
		};
	}, [firePromptInputEngaged]);

	const onPromptContentChange = useCallback(
		(payload: PromptContentChangePayload) => {
			if (payload.isEmpty && !payload.hasMentionOrInlineCard) {
				return;
			}
			firePromptInputEngaged(payload, api);
		},
		[api, firePromptInputEngaged],
	);

	const isSmartlinkToolbarInlinePromptAnalyticsEnabled = expValEqualsNoExposure(
		'platform_editor_rovobutton_smartlink_toolbar_exp',
		'isEnabled',
		true,
	);

	// Memoized key forces RovoInlineChatPrompt to remount when the skill selection changes
	const userADFInputKey = useMemo(
		() => (userADFInput ? JSON.stringify(userADFInput) : undefined),
		[userADFInput],
	);

	// If a session is active - we'll show the loading screen elsewhere and we can hide this
	if (nextSessionId) {
		return null;
	}

	return (
		<FlagsProvider>
			<RovoInlineChatPrompt
				key={userADFInputKey}
				source={source}
				productKey={productKey}
				siteId={cloudId}
				orgId={orgId}
				objectId={targetId}
				_showLabsLozengeVariant={selectionLabsLozengeVariant}
				showMauiMediaPlaceholder={isMauiOrMediaSelection}
				openChatOnError={
					source === 'CONFLUENCE_INLINE_EDITOR' &&
					fg('cc-maui-phase-2') &&
					expValEquals('cc-maui-experiment', 'isEnabled', true)
				}
				onAbort={handleAbort}
				// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
				onCancel={() => {
					/* noop - this is to allow the rovo inline chat to manage cancel behaviour.
					 * We shouldn't end the AI experience directly. */
				}}
				popupsMountPoint={fg('platform_editor_fix_inline_rendering') ? popupsMountPoint : undefined}
				// The following nops usages are like this because we want to ensure that only the AI plugin or the Orchestrator
				// are controlling the xstate. We do not want this to end the ai experience directly. Instead the rovo bridge
				// will call the orchestrator which will then end the experience, on both complete and abort.
				// IMPORTANT!! Do not end the AI experience on the complete callback. This can cause problems because the
				// rovo bridge may still have events to stream in the queue, and it's possible that this could result in
				// the experience being ended immediately on mount, which would then unmount the prompt. This is because the rovo bridge
				// can have a final response in its queue which has never fluushed previously, because something else unmounted this
				// component before the final response was flushed.
				// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
				onComplete={() => {
					setIsRovoLoading(false);
					if (
						// eslint-disable-next-line @atlaskit/platform/no-preconditioning -- gate + experiment layering for MAUI rollout (cc-maui-phase-2 + cc-maui-experiment); will simplify post-rollout
						editorView &&
						fg('cc-maui-phase-2') &&
						expValEqualsNoExposure('cc-maui-experiment', 'isEnabled', true)
					) {
						createEndInlineRovoStreamingCommand()(editorView.state, editorView.dispatch);
					}
				}}
				// When the progress bar killswitch is enabled, the inline chat's
				// onBeforeComplete will delay calling complete() until the progress bar
				// fill-to-100% animation finishes, preventing the UI from flashing the
				// prompt input before the parent unmounts this component.
				// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
				onBeforeComplete={
					isMauiOrMediaSelection
						? (complete) => {
							complete();
						}
						: undefined
				}
				onSubmit={onSubmit}
				onBeforeSubmit={openInRovoChatConfig?.onBeforeSubmit}
				enableAgents={editorPluginAIProvider.isRovoEnabled}
				_showLabsLozenge={showLabsLozenge}
				compact={
					width
						? width <= Breakpoint.Small &&
						expValEquals('platform_editor_comment_rovoinlinechat_improvement', 'isEnabled', true)
						: false
				}
				onPromptContentChange={
					isSmartlinkToolbarInlinePromptAnalyticsEnabled ? onPromptContentChange : undefined
				}
				defaultValue={userADFInput}
			/>
		</FlagsProvider>
	);
}
